import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const refreshIntervalRef = useRef(null);

    const applyUserTheme = (user) => {
        const theme = user?.preferences?.theme || localStorage.getItem('theme') || 'light';
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    };

    const checkAuth = async () => {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (token && storedUser) {
            try {
                let parsedUser = JSON.parse(storedUser);
                
                // Asegurar que los permisos son un array
                if (typeof parsedUser.permissions === 'string') {
                    parsedUser.permissions = JSON.parse(parsedUser.permissions);
                }
                if (!Array.isArray(parsedUser.permissions)) {
                    parsedUser.permissions = parsedUser.permissions ? Object.keys(parsedUser.permissions) : [];
                }
                
                setUser(parsedUser);
                applyUserTheme(parsedUser);
                console.log('✅ Usuario cargado desde localStorage:', parsedUser.email, 'Permisos:', parsedUser.permissions);
            } catch (error) {
                console.error('Error al parsear usuario:', error);
                logout();
            }
        }
        setLoading(false);
    };

    // Refrescar datos del usuario desde el backend para obtener permisos actualizados
    const refreshUser = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            const response = await authService.getProfile();
            let updatedUser = response.data;
            
            // Asegurar que los permisos son un array
            if (typeof updatedUser.permissions === 'string') {
                updatedUser.permissions = JSON.parse(updatedUser.permissions);
            }
            if (!Array.isArray(updatedUser.permissions)) {
                updatedUser.permissions = updatedUser.permissions ? Object.keys(updatedUser.permissions) : [];
            }
            
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(updatedUser);
            console.log('✅ Permisos del usuario refrescados:', updatedUser.permissions, 'para usuario:', updatedUser.email);
        } catch (error) {
            console.error('Error al refrescar usuario:', error);
            // Si el token es inválido, hacer logout
            if (error.response?.status === 401) {
                // Limpiar logout
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setUser(null);
            }
        }
    }, []);

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    useEffect(() => {
        checkAuth();
        
        // Refrescar permisos INMEDIATAMENTE después de cargar
        const initialRefresh = setTimeout(() => {
            refreshUser();
        }, 500);
        
        // Refrescar permisos del usuario cada 10 segundos (muy agresivo para actualizar cambios inmediatos)
        const interval = setInterval(() => {
            if (localStorage.getItem('token')) {
                refreshUser();
            }
        }, 10 * 1000); // 10 segundos

        // Refrescar también cuando el usuario regresa a la pestaña
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && localStorage.getItem('token')) {
                console.log('👁️ Usuario regresó a la pestaña, refrescando permisos...');
                refreshUser();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearTimeout(initialRefresh);
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [refreshUser]);

    const login = async (email, password) => {
        try {
            console.log('🔐 Intentando login desde frontend:', email);
            const response = await authService.login(email, password);
            console.log('📥 Respuesta recibida del backend:', response);
            const { token, user } = response.data;
            console.log('🎟️ Token recibido:', token ? 'SÍ' : 'NO');
            console.log('👤 Usuario recibido:', user);
            
            // Asegurar que los permisos son un array
            let userToSave = { ...user };
            if (typeof userToSave.permissions === 'string') {
                userToSave.permissions = JSON.parse(userToSave.permissions);
            }
            if (!Array.isArray(userToSave.permissions)) {
                userToSave.permissions = userToSave.permissions ? Object.keys(userToSave.permissions) : [];
            }
            
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(userToSave));
            setUser(userToSave);
            applyUserTheme(userToSave);
            console.log('✅ Usuario guardado en estado:', userToSave.email, 'Permisos:', userToSave.permissions);
            
            return { success: true };
        } catch (error) {
            console.error('❌ Error en login:', error);
            return {
                success: false,
                message: error.response?.data?.error || 'Error al iniciar sesión'
            };
        }
    };

    const updateUser = (updatedUser) => {
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
    };

    const hasRole = (...roles) => {
        return user && roles.includes(user.role);
    };

    const value = {
        user,
        loading,
        login,
        logout,
        updateUser,
        refreshUser,
        isAuthenticated: !!user,
        hasRole
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe usarse dentro de AuthProvider');
    }
    return context;
};

export default AuthContext;
